import { Flex, Text, palette } from "@minervaproject/fds";
import { sortBy } from "lodash";
import pluralize from "pluralize";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import {
  PollResponseType,
  PollSessionType,
  PollSessionViewResultsAsType,
  PollType,
} from "shared/types";
import { UserType } from "shared/types/user";
import { longDisplayName } from "utils/user";

const CHART_COLORS = [
  palette.redViolet,
  palette.orange,
  palette.blueGreenShade20,
  palette.violet,
  palette.blackTint40,
  palette.blueShade40,
  palette.blueTint20,
  palette.blackTint90,
  palette.redVioletShade40,
  palette.yellow,
  palette.yellowGreen,
  palette.greenShade40,
];

const tickStyles = {
  fontFamily: "'acumin-pro', sans-serif",
  fontSize: "12px",
  fill: palette.blackTint40,
};

type ChartDataType = {
  id: string;
  choice: string;
  count: number;
  userNames: string[];
};

function PollChartLegend({ data }: { data: ChartDataType[] }) {
  return (
    <Flex direction="column" pl={2}>
      {data.map((item, index) => (
        <Flex
          key={item.id}
          none
          direction="column"
          mb={3}
          pl={2}
          style={{ borderLeft: `4px solid ${CHART_COLORS[index % CHART_COLORS.length]}` }}
        >
          <Text kind="body-s" color="blackTint10">
            {item.choice}
          </Text>
          <Text kind="body-s" color="blackTint40">
            {item.count} {pluralize("vote", item.count)}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

function PollChartTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload) return null;
  const item = payload[0].payload;
  const userNames = item.userNames?.join(", ");

  return (
    <Flex
      direction="column"
      bgColor="blackTint20"
      p={2}
      style={{ borderRadius: "3px", maxWidth: "200px" }}
    >
      <Text
        kind="body-xs"
        color="white"
        bold
        pb={2}
        mb={2}
        style={{ borderBottom: `1px solid ${palette.white}` }}
      >
        {item.choice}
      </Text>
      <Text kind="body-xs" color="white">
        {item.count} {pluralize("vote", item.count)}
        {userNames && `: ${userNames}`}
      </Text>
    </Flex>
  );
}

function PollBarChart({ data }: { data: ChartDataType[] }) {
  const labelFormatter = (value: string) => {
    const totalWidth =
      document.getElementsByClassName("recharts-cartesian-grid")[0] &&
      document.getElementsByClassName("recharts-cartesian-grid")[0].getBoundingClientRect().width;
    const barWidth = totalWidth / data.length;
    const maxCharacters = barWidth / 6 - 3; // 6 = approximate length of a character, subtract another 3 for the ellipsis
    if (value.length < maxCharacters) return value;
    return `${value.substring(0, maxCharacters)}...`;
  };

  return (
    <ResponsiveContainer width="95%" height="95%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="choice"
          interval={0}
          stroke={palette.blackTint90}
          tick={tickStyles}
          tickFormatter={labelFormatter}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          axisLine={false}
          tick={tickStyles}
          tickLine={false}
          domain={[0, "dataMax"]}
        />
        <Tooltip content={<PollChartTooltip />} cursor={false} />
        <Bar dataKey="count">
          {data.map((item, index) => (
            <Cell key={`cell-${item.id}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function PollPieChart({ data, isEmpty }: { data: ChartDataType[]; isEmpty: boolean }) {
  return (
    <ResponsiveContainer width="95%" height="95%">
      <PieChart>
        {isEmpty ? (
          <Pie
            data={[{ choice: "no data", count: 1 }]}
            dataKey="count"
            innerRadius="50%"
            outerRadius="90%"
            fill={palette.blackTint90}
          />
        ) : (
          <>
            <Legend
              content={<PollChartLegend data={data} />}
              layout="vertical"
              verticalAlign="middle"
              align="right"
              width={200}
              wrapperStyle={{
                maxHeight: "100%",
                overflowY: "auto",
                padding: "0 4px",
              }}
            />
            <Tooltip content={<PollChartTooltip />} cursor={false} />
            <Pie data={data} dataKey="count" innerRadius="50%" outerRadius="90%">
              {data.map((item, index) => (
                <Cell key={`cell-${item.id}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
          </>
        )}
      </PieChart>
    </ResponsiveContainer>
  );
}

type PollChartProps = {
  poll: PollType;
  pollSession: PollSessionType;
  pollResponses: PollResponseType[];
  users: { [id: string]: UserType };
};

function PollChart({ poll, pollSession, pollResponses, users }: PollChartProps) {
  const choices = sortBy(poll.poll_choices, "index");
  const data: ChartDataType[] = [];
  choices.forEach((choice) => {
    const responsesForChoice = pollResponses.filter(
      (pollResponse) =>
        (pollResponse.response as number[]).includes(choice.index) && !pollResponse.is_draft,
    );
    data.push({
      id: choice.id,
      choice: choice.choice,
      count: responsesForChoice.length,
      userNames: responsesForChoice.map((pollResponse) =>
        longDisplayName(users[pollResponse.user_id]),
      ),
    });
  });
  const allResponses = pollResponses.map((pollResponse) => pollResponse.response).flat();

  return (
    <div
      className="flex flex-auto"
      style={{
        minHeight: "150px",
        minWidth: "100%",
      }}
      data-testid="poll-chart"
    >
      {pollSession.view_results_as === PollSessionViewResultsAsType.Bar ? (
        <PollBarChart data={data} />
      ) : (
        <PollPieChart data={data} isEmpty={allResponses.length === 0} />
      )}
    </div>
  );
}

export { CHART_COLORS, PollChartLegend, PollChartTooltip, PollBarChart, PollPieChart, PollChart };
